import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Box, Paper, Stack } from "@mui/material";
import {
  setBrandsData,
  setBrandsLoading,
  setBrandsMtdData,
  setBrandsMtdLoading,
} from "../../../../../../../../features/optifundDashboardSlice";
import { OptifundDashboardQueryIds } from "../../../../../../../../pages/AssetManagement/Overview/OptifundDashboard/logic/OptifundDashboardQueryIds";
import { useOptifundTabData } from "../../../../hooks/useOptifundTabData";
import { BrandDailyRow, BrandMtdRow, mapBrandDailyApiRow, mapBrandMtdApiRow } from "../../../../Brands/brandsApiMapping";
import BrandsSelector from "./BrandsMobileContent/BrandsSelector";
import BrandsKpiCards from "./BrandsMobileContent/BrandsKpiCards";
import BrandsIncomeChart from "./BrandsMobileContent/BrandsIncomeChart";
import BrandsDailyTable from "./BrandsMobileContent/BrandsDailyTable";
import BrandsMtdTable from "./BrandsMobileContent/BrandsMtdTable";
import BrandsSkel from "./BrandsMobileContent/helpers/BrandsSkel";

const ALL_BRANDS = "All brands";
const EMPTY_DAILY_ROWS: BrandDailyRow[] = [];
const EMPTY_MTD_ROWS: BrandMtdRow[] = [];

interface BrandsProps {
  userToken: any;
  websiteSharedUid: string;
}

// Mirrors the desktop Brands.tsx's data-fetching wiring exactly (same query
// IDs, same Redux actions, same "all" brand param + client-side filtering).
// Only the rendered content differs (checklist Section 10.3/10.4).
export default function Brands({ userToken, websiteSharedUid }: BrandsProps) {
  const dispatch = useDispatch();
  const { brandsData, brandsLoading, brandsMtdData, brandsMtdLoading } = useSelector(
    (state: any) => state.optifundDashboard
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedBrand, setSelectedBrand] = useState(() => searchParams.get("brand") ?? ALL_BRANDS);

  const handleSelectedBrandChange = (brand: string) => {
    setSelectedBrand(brand);
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        if (brand === ALL_BRANDS) next.delete("brand");
        else next.set("brand", brand);
        return next;
      },
      { replace: true }
    );
  };

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsDailyId,
    (data) => dispatch(setBrandsData(data)),
    (loading) => dispatch(setBrandsLoading(loading)),
    undefined,
    undefined,
    "all"
  );

  useOptifundTabData(
    userToken,
    websiteSharedUid,
    OptifundDashboardQueryIds.brandsMtdId,
    (data) => dispatch(setBrandsMtdData(data)),
    (loading) => dispatch(setBrandsMtdLoading(loading)),
    undefined,
    undefined,
    "all"
  );

  const dailyRows: BrandDailyRow[] = useMemo(
    () => (Array.isArray(brandsData) ? brandsData.map(mapBrandDailyApiRow) : EMPTY_DAILY_ROWS),
    [brandsData]
  );
  const mtdRows: BrandMtdRow[] = useMemo(
    () => (Array.isArray(brandsMtdData) ? brandsMtdData.map(mapBrandMtdApiRow) : EMPTY_MTD_ROWS),
    [brandsMtdData]
  );

  const brandOptions = useMemo(() => {
    const uniqueBrands = Array.from(
      new Set([...dailyRows.map((row) => row.brand), ...mtdRows.map((row) => row.brand)])
    ).sort();
    return [ALL_BRANDS, ...uniqueBrands];
  }, [dailyRows, mtdRows]);

  const filteredDailyRows = useMemo(
    () => dailyRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [dailyRows, selectedBrand]
  );
  const filteredMtdRows = useMemo(
    () => mtdRows.filter((row) => selectedBrand === ALL_BRANDS || row.brand === selectedBrand),
    [mtdRows, selectedBrand]
  );

  const isLoading =
    brandsLoading || brandsMtdLoading || !Array.isArray(brandsData) || !Array.isArray(brandsMtdData);

  if (isLoading) {
    return <BrandsSkel />;
  }

  return (
    <Stack spacing={2.5}>
      <Paper variant="outlined" sx={{ p: "13px 14px", borderColor: "#ececec" }}>
        <Box sx={{ maxWidth: 320 }}>
          <BrandsSelector options={brandOptions} selectedBrand={selectedBrand} onSelectedBrandChange={handleSelectedBrandChange} />
        </Box>
      </Paper>
      <BrandsKpiCards dailyRows={filteredDailyRows} mtdRows={filteredMtdRows} />
      <BrandsIncomeChart rows={filteredMtdRows} />
      <BrandsDailyTable rows={filteredDailyRows} />
      <BrandsMtdTable rows={filteredMtdRows} />
    </Stack>
  );
}
